import { makePersisted } from "@solid-primitives/storage";
import { A, useNavigate } from "@solidjs/router";
import { useBillingClient, useCallOptions, withBearerToken } from "@storytell/cp-client";
import { subscribeToGlobalEvent } from "@storytell/events";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { TbCalendarEvent, TbHelpCircle, TbLoader2, TbTrendingUp, TbWallet, TbX } from "solid-icons/tb";
import { type Component, Show, Suspense, createSignal, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";
import { useWire } from "~/wire";
import { usePromptBarContext } from "../PromptBarContext";
import HexagonSparkleIcon from "./HexagonSparkleIcon";
import { PromptBarButton } from "./PromptBarButton";

/**
 * Credits display component with pill and detailed modal
 * Matches the styling of the Settings panel
 */
export const CreditsDisplay: Component = () => {
  const wire = useWire();
  const navigate = useNavigate();
  const { credits, knowledgeSources } = usePromptBarContext();

  // Track if popup was auto-opened (for showing dismiss button)
  const [isAutoOpened, setIsAutoOpened] = createSignal(false);

  // Cookie to track if user has dismissed auto-popup
  const [hasDismissedAutoPopup, setHasDismissedAutoPopup] = makePersisted(createSignal(false), {
    storage: isServer ? undefined : localStorage,
    name: "credits-auto-popup-dismissed",
  });

  // Calculate percentage used
  const percentageUsed = () => {
    const remaining = wire.billing.credits();
    const used = wire.billing.creditsUsedThisPeriod();
    const total = used + remaining; // Total = used + remaining

    if (total === 0) return 0; // No credits allocated or used at all
    return Math.min(100, Math.round((used / total) * 100));
  };

  // Calculate percentage remaining
  const percentageRemaining = () => {
    return Math.max(0, 100 - percentageUsed());
  };

  // Popover open/close is managed by StPopover; keep pricing/knowledge panel coordination here

  // Auto-show popup when credits are low after prompt submission
  onMount(() => {
    const unsubscribePromptSubmitted = subscribeToGlobalEvent("prompt-submitted-successfuly", () => {
      // Check if credits are at or below 20% remaining and user hasn't dismissed auto-popup
      if (percentageRemaining() <= 20 && !hasDismissedAutoPopup()) {
        // Mark as auto-opened and show the credits popup
        setIsAutoOpened(true);
        credits.setShowTooltip(true);
        // Close knowledge sources popup if it's open
        if (knowledgeSources.showTooltip()) {
          knowledgeSources.setShowTooltip(false);
        }
      }
    });

    onCleanup(() => {
      unsubscribePromptSubmitted();
    });
  });

  // Get color based on usage
  const getUsageColor = () => {
    const percent = percentageUsed();
    if (percent < 50) return "text-emerald-500";
    if (percent < 80) return "text-yellow-500";
    return "text-red-500";
  };

  // Get icon color based on remaining credits
  const getIconColor = () => {
    const remaining = percentageRemaining();
    if (remaining >= 65) return "text-primary-text"; // White/default text color
    if (remaining >= 21) return "text-amber-500";
    return "text-red-500";
  };

  // Get button background classes based on remaining credits
  const getButtonClasses = () => {
    const remaining = percentageRemaining();
    if (remaining >= 65) {
      return "bg-panel/80 border-primary-border hover:bg-panel-hover";
    }
    if (remaining >= 21) {
      return "bg-gradient-to-br from-amber-500/10 to-amber-600/10 border-amber-500/30 hover:from-amber-500/20 hover:to-amber-600/20";
    }
    return "bg-gradient-to-br from-amber-500/10 to-red-600/10 border-red-500/30 hover:from-amber-500/20 hover:to-red-600/20";
  };

  // Get status dot color
  const getStatusDotClasses = () => {
    const remaining = percentageRemaining();
    if (remaining <= 20) return "bg-red-500 animate-pulse";
    if (remaining <= 64) return "bg-amber-500";
    return "bg-emerald-500";
  };

  // Get percentage remaining color (same as dot color)
  const getPercentageColor = () => {
    const remaining = percentageRemaining();
    if (remaining <= 20) return "text-red-500";
    if (remaining <= 64) return "text-amber-500";
    return "text-emerald-500";
  };

  return (
    <Show when={wire.billing.show()}>
      <StPopover
        noArrow
        open={credits.showTooltip()}
        onOpenChange={(open) => {
          if (open && knowledgeSources.showTooltip()) {
            knowledgeSources.setShowTooltip(false);
          }
          // If manually opening, reset auto-opened state
          if (open && !isAutoOpened()) {
            setIsAutoOpened(false);
          }
          credits.setShowTooltip(open);
        }}
        gutter={10}
        placement="top-end"
        flip
        contentProps={{ class: "w-80 bg-panel border border-primary-border rounded-xl shadow-xl" }}
        content={
          <Suspense fallback={<div class="p-4">Loading...</div>}>
            <div class="p-4 space-y-3 relative">
              {/* Dismiss button - Only show when auto-opened OR when credits are low (for testing) */}
              <Show when={isAutoOpened() || percentageRemaining() <= 20}>
                <button
                  type="button"
                  onClick={() => {
                    setHasDismissedAutoPopup(true);
                    credits.setShowTooltip(false);
                    setIsAutoOpened(false);
                  }}
                  class="absolute top-0 -right-1 w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  aria-label="Dismiss and don't show again"
                >
                  <TbX class="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
              </Show>

              {/* Low Credits Warning - Only show when ≤20% remaining */}
              <Show when={percentageRemaining() <= 20}>
                <div class="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-2">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 bg-red-500 rounded-full" />
                    <span class="text-sm text-red-800 dark:text-red-200">
                      <button
                        type="button"
                        onClick={() => {
                          credits.hideTooltip();
                          navigate("/pricing");
                        }}
                        class="font-medium underline hover:underline-offset-2 cursor-pointer focus:outline-none"
                      >
                        {percentageRemaining() === 0 ? "Refill Now" : "Refill Soon"}
                      </button>
                      {percentageRemaining() === 0 ? " to keep using Storytell" : ": Almost out of credits"}
                    </span>
                  </div>
                </div>
              </Show>

              {/* Header */}
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-primary-text">
                  {wire.billing.isProPlan() ? "Pro Plan" : "Explore Plan (Free)"}
                </span>
                <button
                  type="button"
                  class="text-xs text-brand underline hover:underline-offset-2 cursor-pointer focus:outline-none"
                  onClick={async () => {
                    credits.hideTooltip();
                    if (wire.billing.isFreePlan()) {
                      navigate("/pricing");
                      return;
                    }

                    const client = useBillingClient();
                    const res = await client.createPortalSession(
                      {
                        returnUrl: `https://${APP_DOMAIN}/`,
                        customerId: wire.auth.user()?.id,
                      },
                      useCallOptions(withBearerToken(wire.auth.token())),
                    );
                    if (res.isOk() && res.value.session.url) {
                      window.location.href = res.value.session.url;
                    }
                  }}
                >
                  {wire.billing.isProPlan() ? "Manage" : "Upgrade"}
                </button>
              </div>

              <div class="pb-3 border-b border-primary-border space-y-3">
                {/* Credits Balance */}
                <div class="flex items-start gap-3">
                  <TbWallet class={`w-5 h-5 ${getUsageColor()} mt-0.5 flex-shrink-0`} />
                  <div class="flex-1">
                    <div class="text-sm font-medium text-primary-text">Credits Balance</div>
                    <div class="text-xs text-secondary-text mt-0.5">
                      <span class="font-bold text-primary-text">{wire.billing.credits().toLocaleString()}</span> credits
                      available ({percentageRemaining()}%)
                    </div>
                  </div>
                </div>

                {/* Usage This Month */}
                <div class="flex items-start gap-3">
                  <TbTrendingUp class="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div class="flex-1">
                    <div class="text-sm font-medium text-primary-text">Usage This Month</div>
                    <div class="text-xs text-secondary-text mt-0.5">
                      <span class="font-bold text-primary-text">
                        {wire.billing.creditsUsedThisPeriod().toLocaleString()}
                      </span>{" "}
                      credits used ({percentageUsed()}%)
                    </div>
                    <div class="mt-2">
                      <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden border border-gray-300 dark:border-gray-600">
                        <div
                          class={`h-full ${
                            percentageRemaining() >= 65
                              ? "bg-emerald-500"
                              : percentageRemaining() >= 21
                                ? "bg-amber-500"
                                : "bg-red-500"
                          } transition-all duration-300 rounded-full`}
                          style={{ width: `${percentageUsed()}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pro Plan Info */}
                <Show when={wire.billing.isProPlan()}>
                  <div class="flex items-start gap-3">
                    <TbCalendarEvent class="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div class="text-sm font-medium text-primary-text">Pro Plan</div>
                      <div class="text-xs text-secondary-text mt-0.5">Monthly renewal with additional credits</div>
                    </div>
                  </div>
                </Show>
              </div>

              <div class="space-y-2 pt-1">
                <Show
                  when={!wire.auth.isGuest()}
                  fallback={
                    <button
                      type="button"
                      onClick={() => navigate("/auth/signup")}
                      class="w-full px-3 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors text-sm font-medium"
                    >
                      Sign up to get {(10000).toLocaleString()} free credits
                    </button>
                  }
                >
                  <button
                    type="button"
                    onClick={() => navigate("/pricing")}
                    class="w-full px-3 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors text-sm font-medium"
                  >
                    Refill
                  </button>
                </Show>

                <a
                  class="inline-flex items-center gap-1 text-xs text-brand underline hover:underline-offset-2"
                  href="https://go.storytell.ai/learn-credits"
                  target="_blank"
                  rel="noreferrer"
                >
                  <TbHelpCircle class="w-3.5 h-3.5" />
                  Learn how credits work
                </a>
              </div>
            </div>
          </Suspense>
        }
      >
        <span class="relative">
          <StPopoverTrigger
            as={PromptBarButton}
            icon={HexagonSparkleIcon}
            iconClass={`${getIconColor()} transition-all duration-300 group-hover/btn:scale-interaction group-active/btn:scale-active ${percentageRemaining() <= 20 ? "animate-pulse" : ""}`}
            class={`${getButtonClasses()} transition-all duration-300 flex items-center gap-2 px-3`}
            aria-label="Credits"
          >
            <span class={`text-xs font-medium ${getPercentageColor()} transition-all duration-300`}>
              {percentageRemaining()}%
            </span>
          </StPopoverTrigger>
          <div class={`absolute top-0 right-0 w-2.5 h-2.5 rounded-full ${getStatusDotClasses()} pointer-events-none`} />
        </span>
      </StPopover>
    </Show>
  );
};
